import { CameraIntegration, getContactSupportMessage, formatTitle } from './base';
import axios from 'axios';
import dayjs from 'dayjs';
import { post } from '../../helpers/securityService';
import { i18n } from '@qsrsoft/myq-core';

const messages = {
  en: {
    itech_auth_failed: 'We were unable to authenticate your 3xLogic account. Please try again later.',
    itech_camera_list_failed: 'We were unable to connect to 3xLogic in order to view the list of cameras associated with this event. {contactSupportMessage}',
    itech_no_cameras_found: 'We were unable to find any cameras associated with your 3xLogic account. Please contact 3xLogic support.',
    itech_camera_not_found: 'We were unable to find the video for this camera because this camera was not found to be associated with your 3xLogic account. {contactSupportMessage}',
    itech_camera_id_not_found: 'We were unable to identify the selected camera in order to display the associated video. {contactSupportMessage}',
  },
  es: {
    itech_auth_failed: 'No pudimos autenticar su cuenta de 3xLogic. Por favor, inténtelo de nuevo más tarde.',
    itech_camera_list_failed: 'No pudimos conectarnos a 3xLogic para ver la lista de cámaras asociadas con este evento. {contactSupportMessage}',
    itech_no_cameras_found: 'No pudimos encontrar cámaras asociadas con su cuenta de 3xLogic. Comuníquese con el soporte de 3xLogic.',
    itech_camera_not_found: 'No pudimos encontrar el video de esta cámara porque no se encontró que estuviera asociada con su cuenta de 3xLogic. {contactSupportMessage}',
    itech_camera_id_not_found: 'No pudimos identificar la cámara seleccionada para mostrar el video asociado. {contactSupportMessage}',
  },
  fr: {
    itech_auth_failed: "Nous n'avons pas pu authentifier votre compte 3xLogic. Veuillez réessayer plus tard.",
    itech_camera_list_failed: "Nous n'avons pas pu nous connecter à 3xLogic pour afficher la liste des caméras associées à cet événement. {contactSupportMessage}",
    itech_no_cameras_found: "Nous n'avons trouvé aucune caméra associée à votre compte 3xLogic. Veuillez contacter le support 3xLogic.",
    itech_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte 3xLogic. {contactSupportMessage}",
    itech_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. {contactSupportMessage}",
  },
  'fr-ca': {
    itech_auth_failed: "Nous n'avons pas pu authentifier votre compte 3xLogic. Veuillez réessayer plus tard.",
    itech_camera_list_failed: "Nous n'avons pas pu nous connecter à 3xLogic pour afficher la liste des caméras associées à cet événement. {contactSupportMessage}",
    itech_no_cameras_found: "Nous n'avons trouvé aucune caméra associée à votre compte 3xLogic. Veuillez contacter le support 3xLogic.",
    itech_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte 3xLogic. {contactSupportMessage}",
    itech_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. {contactSupportMessage}",
  },
};

Object.keys(messages).forEach((locale) => {
  i18n.global.mergeLocaleMessage(locale, messages[locale]);
});

export class Itech extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'itech';

    this.cameraList = null;
    this.fetchingCameraList = null; // null | Promise
  }

  async submitCredentials(username, password) {
    const {
      config: { vigilconnectalias: dvrSerial },
      location,
    } = this.camera;

    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/itech_submit_creds/${location}/${dvrSerial}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(i18n.global.t('itech_auth_failed'));
    });

    this.camera.accessToken = response.accessToken;
    this.isAuthenticated = true;
  }

  async getCameras() {
    if (!this.cameraList) {
      if (!this.fetchingCameraList) {
        this.fetchingCameraList = this.fetchCameraList();
      }
      await this.fetchingCameraList;
    }
  }

  async fetchCameraList() {
    const errMessage = i18n.global.t('itech_camera_list_failed', {
      contactSupportMessage: getContactSupportMessage(),
    });
    const { accessToken, apiHost } = this.camera;

    const token = btoa(`token:${accessToken}`);
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const { data } = await axios
      .get(`${apiHost}/api/cameras`, { headers })
      .catch((err) => {
        console.error(errMessage, { err });
        throw Error(errMessage);
      });

    if (!data?.data?.length) {
      console.error('No cameras found in the camera list', { data });
      throw Error(i18n.global.t('itech_no_cameras_found'));
    }

    if (data.statuscode !== 200) {
      console.error(errMessage, data.errormessage);
      throw Error(errMessage);
    }

    this.cameraList = data.data;
  }

  async fetchThumbnails() {
    const { apiHost, accessToken } = this.camera;
    await this.getCameras();

    let { time } = this.event;
    // When coming from alerts time is in YYYYMMDDHHMMSS format so we need to convert it
    // otherwise it it already in YYYY-MM-DD HH:MM:SS format
    if (time && time.length === 14 && /^\d{14}$/.test(time)) {
      time = dayjs(time, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss');
    }
    // Store the formatted time as an instance variable for use in other methods
    this.formattedTime = time;

    return this.cameraList.map((camera) => ({
      src: `${apiHost}/api/cameras/${
        camera.index
      }/image/${time}?isUtcTime=0&accessToken=${encodeURIComponent(
        accessToken,
      )}`,
      title: formatTitle(camera.name),
      id: camera.index,
    }));
  }

  async fetchVideo(cameraId, seekTime) {
    const nowPlaying = {
      offsetSeconds: 0,
      seekButtons: true,
      displayTime: this.formattedTime,
    };

    const { videoHost, accessToken, commonSettings } = this.camera;

    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    await this.getCameras();

    if (cameraId)
      nowPlaying.camera = this.cameraList.find(
        (camera) => camera.index === cameraId,
      );
    else [nowPlaying.camera] = this.cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList: this.cameraList },
      );
      throw Error(
        i18n.global.t('itech_camera_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    const videoDuration = commonSettings?.videoDurationSeconds || 30;
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('YYYY-MM-DD HH:mm:ss');

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.camera.name} from ${nowPlaying.displayTime} to ${videoEndTime}`;
    nowPlaying.cameraId = nowPlaying.camera.index;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        i18n.global.t('itech_camera_id_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    const params = {
      token: accessToken,
      camera: nowPlaying.cameraId,
      from: nowPlaying.displayTime,
      to: videoEndTime,
      resolution: '1024x768',
      skipframes: 1,
      speed: 5,
      quality: 70
    };
    nowPlaying.video = {
      src: `${videoHost}?${new URLSearchParams(params).toString()}`,
      type: 'iframe',
    };

    return nowPlaying;
  }
}
